import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import ModalCita from "./ModalCita";
import FichaResumenPropiedad from "./propiedades/FichaResumenPropiedad";
import { dialogs } from "../context/DialogContext";
import { addMinutosHora, buildCitaDateTimeISO, fetchPropiedadPorRef } from "../utils/citaAgenda";

function inicialesContacto(nombre) {
  const trozos = String(nombre || "").trim().split(/\s+/).filter(Boolean);
  return (trozos.map((x) => x[0]).join("").slice(0, 2) || "CL").toUpperCase();
}

function mapPropiedadParaCita(p) {
  if (!p) return null;
  let propietarios = [];
  if (Array.isArray(p.propietarios)) propietarios = p.propietarios;
  else if (typeof p.propietarios === "string") {
    try {
      propietarios = JSON.parse(p.propietarios);
    } catch {
      propietarios = [];
    }
  }
  return {
    id: p.id,
    titulo: p.titulo || p.tipo_nombre || "Sin título",
    detalle: `${p.tipo_nombre || p.tipo || ""} · ${p.poblacion_nombre || p.poblacion || ""}`.trim(),
    precio: p.precio ? `${Number(p.precio).toLocaleString("es-ES")} €` : "Consultar",
    referencia: p.referencia,
    tenemos_llaves: p.tenemos_llaves || false,
    num_llave: p.num_llave || null,
    recoger_llaves_en: p.recoger_llaves_en || null,
    propietarios,
    info_visita: p.info_visita != null ? String(p.info_visita) : "",
  };
}

async function fetchPropiedadCompletaParaCita(p) {
  if (!p?.id) return mapPropiedadParaCita(p);
  try {
    const [detRes, propRes] = await Promise.all([
      api.get(`/propiedades/${p.id}`),
      api.get(`/propiedades/${p.id}/propietarios`).catch(() => ({ data: { data: [] } })),
    ]);
    const det = detRes.data?.data || detRes.data?.propiedad || detRes.data || p;
    const propietarios = propRes.data?.data || propRes.data || [];
    return mapPropiedadParaCita({ ...det, propietarios });
  } catch {
    return mapPropiedadParaCita(p);
  }
}

/**
 * Modal de cita para leads/clientes en fríos.
 * lead: fila del lead (Pipeline) o lead resuelto desde cliente.
 * cliente: opcional, para ruta POST /cliente/:id/cita-desde-frio
 */
export default function CitaDesdeFrioModal({
  open,
  onClose,
  onSuccess,
  lead,
  cliente = null,
  usuario = null,
  agentName = "",
  onLlamarTelefono,
}) {
  const navigate = useNavigate();
  const [propiedadesCita, setPropiedadesCita] = useState([]);
  const [cargandoProps, setCargandoProps] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [vistaPreviaPropId, setVistaPreviaPropId] = useState(null);

  const nombreContacto = cliente
    ? [cliente.nombre, cliente.apellido].filter(Boolean).join(" ").trim()
    : [lead?.nombre, lead?.apellido].filter(Boolean).join(" ").trim() || "Cliente";

  const telefonoContacto = String(
    lead?.telefono || lead?.whatsapp || cliente?.telefono || cliente?.whatsapp || ""
  ).trim();
  const emailContacto = String(lead?.email || cliente?.email || "").trim();

  const cargarPropiedadesParaCita = useCallback(async () => {
    setCargandoProps(true);
    try {
      // Igual que al crear la cita desde la ficha cliente: arrancamos solo con la
      // propiedad de origen del lead (si existe). El agente añade las que toquen
      // por referencia con el "+ Añadir"; no volcamos un listado genérico.
      const lista = [];
      const ref = String(lead?.propiedad_ref || "").trim();
      if (ref) {
        try {
          const r = await api.get(`/propiedades/validar-ref/${encodeURIComponent(ref)}`);
          const p = r.data?.propiedad || r.data?.data;
          if (p?.id) {
            const mapped = await fetchPropiedadCompletaParaCita(p);
            if (mapped) lista.push(mapped);
          }
        } catch {
          /* sin ref válida */
        }
      }
      setPropiedadesCita(lista);
    } catch {
      setPropiedadesCita([]);
    } finally {
      setCargandoProps(false);
    }
  }, [lead?.propiedad_ref]);

  useEffect(() => {
    if (open) cargarPropiedadesParaCita();
    else setPropiedadesCita([]);
  }, [open, cargarPropiedadesParaCita]);

  const handleConfirm = async (data) => {
    try {
      if (data?.cierreMode === "ana") {
        throw new Error("Ana (IA) aún no está activa. Elige «Yo mismo» para programar la visita.");
      }
      const propsVisita = data?.propiedades || [];

      const agenteId = usuario?.agente_id || null;
      if (!agenteId) {
        throw new Error("Inicia sesión como agente para crear la cita y reasignar el lead.");
      }

      const fechaInicio = buildCitaDateTimeISO(data.fecha, data.horaInicio);
      const bloques = Math.max(1, propsVisita.length);
      const horaFin = data.horaFin || addMinutosHora(data.horaInicio, bloques * (data.duracion || 45));
      const fechaFin = buildCitaDateTimeISO(data.fecha, horaFin);
      if (!fechaInicio || !fechaFin) throw new Error("Fecha u hora de la visita no válidas.");

      const propiedadIds = propsVisita.map((p) => p.id).filter(Boolean);
      const lineasHorario = propsVisita.map((p) => {
        const ref = p.referencia || p.id;
        return `${p.horaVisita || data.horaInicio} · Ref. ${ref}`;
      });

      setGuardando(true);
      const agenda = {
        agente_id: String(agenteId),
        lead_id: lead?.id || null,
        cliente_id: lead?.cliente_id || cliente?.id || null,
        propiedad_ids: propiedadIds.length ? propiedadIds : null,
        titulo: `Visita — ${nombreContacto}`,
        descripcion: propsVisita.length
          ? `Visita programada desde fríos.\n${lineasHorario.join("\n")}`
          : "Visita programada desde fríos.",
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      };

      const url = lead?.id
        ? `/tareas/lead/${lead.id}/cita-desde-frio`
        : `/tareas/cliente/${cliente.id}/cita-desde-frio`;

      const { data: result } = await api.post(url, { agenda });
      if (result?.ok === false) {
        await dialogs.alert(result?.error || "No se pudo crear la cita");
        return;
      }
      if (!result?.nextTask?.id && !result?.tarea?.id) {
        await dialogs.alert(
          "La cita se guardó, pero no apareció la tarea T4 · Cita. Recarga Tareas o contacta con soporte."
        );
      }
      await onSuccess?.(result);
      onClose?.();
    } catch (e) {
      await dialogs.alert(e?.response?.data?.error || e.message || "No se pudo crear la cita");
    } finally {
      setGuardando(false);
    }
  };

  if (!open) return null;

  return (
    <>
    <ModalCita
      isOpen={open}
      zIndex={1300}
      onClose={guardando ? undefined : onClose}
      onConfirm={handleConfirm}
      contact={{
        nombre: nombreContacto,
        telefono: telefonoContacto,
        email: emailContacto,
        iniciales: inicialesContacto(nombreContacto),
        clienteId: cliente?.id || lead?.cliente_id || null,
      }}
      properties={cargandoProps ? [] : propiedadesCita}
      agentName={agentName || usuario?.nombre || ""}
      onLlamarTelefono={onLlamarTelefono}
      onAbrirVistaPrevia={setVistaPreviaPropId}
      onAbrirFichaCliente={(clienteId) => { if (clienteId) navigate(`/clientes/${clienteId}`); }}
      onAbrirFichaContacto={(contactoId) => { if (contactoId) navigate(`/contactos/${contactoId}`); }}
      onBuscarPropiedadPorRef={(ref) => fetchPropiedadPorRef(api, ref)}
    />
    {vistaPreviaPropId && (
      <FichaResumenPropiedad propiedadId={vistaPreviaPropId} onClose={() => setVistaPreviaPropId(null)} />
    )}
    </>
  );
}
